import React, { useState, useEffect } from 'react';
import {
    SparklesIcon,
    ChartBarIcon,
    LightBulbIcon,
    BeakerIcon,
    CpuChipIcon,
    EyeIcon
} from '@heroicons/react/24/outline';

const CurationIntelligence = ({ themes, projects, onThemeRecommendation }) => {
    const [aiInsights, setAiInsights] = useState(null);
    const [trendAnalysis, setTrendAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);

    const generateAIInsights = async () => {
        setLoading(true);

        // Simulate AI analysis
        setTimeout(() => {
            setAiInsights({
                thematic_coherence_score: 8.7,
                market_positioning: "Ultra-luxury avant-garde",
                recommended_price_points: [1200, 2800, 4500, 8900],
                cultural_relevance: 9.2,
                commercial_risk_assessment: "Medium-High",
                seasonal_alignment: "Strong alignment with FW24 deconstructed trends",
                competitor_analysis: {
                    similar_concepts: ["Junya Watanabe's architectural approach", "Issey Miyake's technical innovation"],
                    differentiation_score: 8.4
                },
                target_demographics: [
                    { segment: "Fashion Intellectuals", affinity: 94 },
                    { segment: "Art Collectors", affinity: 87 },
                    { segment: "Creative Directors", affinity: 91 }
                ]
            });

            setTrendAnalysis({
                emerging_movements: [
                    { name: "Neo-Brutalist Fashion", growth: "+127%", relevance: "High" },
                    { name: "Sustainable Deconstruction", growth: "+89%", relevance: "Medium" },
                    { name: "Digital-Physical Hybrid", growth: "+156%", relevance: "High" }
                ],
                material_innovations: [
                    { name: "Bio-fabricated Leather", adoption: "Early", potential: "Revolutionary" },
                    { name: "3D-Knitted Structures", adoption: "Growing", potential: "High" },
                    { name: "Smart Textiles", adoption: "Experimental", potential: "Transformative" }
                ],
                cultural_zeitgeist: {
                    dominant_themes: ["Post-pandemic minimalism", "Digital nomad aesthetics", "Climate consciousness"],
                    influence_score: 8.9
                }
            });

            setLoading(false);
        }, 2000);
    };

    const AIInsightCard = ({ title, value, description, trend }) => (
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
            <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{title}</h4>
                {trend && (
                    <span className={`text-xs px-2 py-1 rounded ${trend > 0 ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                            'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                        {trend > 0 ? '+' : ''}{trend}%
                    </span>
                )}
            </div>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">
                {value}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">{description}</p>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* AI Analysis Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <CpuChipIcon className="w-8 h-8 text-purple-500" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                AI Curation Intelligence
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Advanced analytics for professional fashion curation
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={generateAIInsights}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors disabled:opacity-50"
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                            <BeakerIcon className="w-4 h-4" />
                        )}
                        {loading ? 'Analyzing...' : 'Generate AI Insights'}
                    </button>
                </div>

                {loading && (
                    <div className="text-center py-8">
                        <div className="animate-pulse space-y-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></div>
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 mx-auto"></div>
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-2/3 mx-auto"></div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                            Analyzing thematic coherence, market positioning, and cultural relevance...
                        </p>
                    </div>
                )}
            </div>

            {/* AI Insights Grid */}
            {aiInsights && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <AIInsightCard
                        title="Thematic Coherence"
                        value={`${aiInsights.thematic_coherence_score}/10`}
                        description="AI-assessed conceptual consistency across collection"
                    />
                    <AIInsightCard
                        title="Cultural Relevance"
                        value={`${aiInsights.cultural_relevance}/10`}
                        description="Alignment with current cultural zeitgeist"
                    />
                    <AIInsightCard
                        title="Market Position"
                        value={aiInsights.market_positioning}
                        description="Predicted market segment and positioning"
                    />
                    <AIInsightCard
                        title="Risk Assessment"
                        value={aiInsights.commercial_risk_assessment}
                        description="Commercial viability risk evaluation"
                    />
                </div>
            )}

            {/* Trend Analysis */}
            {trendAnalysis && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <ChartBarIcon className="w-5 h-5 text-blue-500" />
                            Emerging Fashion Movements
                        </h3>
                        <div className="space-y-3">
                            {trendAnalysis.emerging_movements.map((movement, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                                    <div>
                                        <div className="font-medium text-gray-900 dark:text-white text-sm">
                                            {movement.name}
                                        </div>
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                            Relevance: {movement.relevance}
                                        </div>
                                    </div>
                                    <div className="text-green-600 dark:text-green-400 font-bold text-sm">
                                        {movement.growth}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <BeakerIcon className="w-5 h-5 text-green-500" />
                            Material Innovations
                        </h3>
                        <div className="space-y-3">
                            {trendAnalysis.material_innovations.map((material, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                                    <div>
                                        <div className="font-medium text-gray-900 dark:text-white text-sm">
                                            {material.name}
                                        </div>
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                            Adoption: {material.adoption}
                                        </div>
                                    </div>
                                    <div className="text-purple-600 dark:text-purple-400 font-bold text-xs">
                                        {material.potential}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Target Demographics Analysis */}
            {aiInsights && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <EyeIcon className="w-5 h-5 text-indigo-500" />
                        Target Demographic Affinity Analysis
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {aiInsights.target_demographics.map((demo, idx) => (
                            <div key={idx} className="text-center p-4 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg">
                                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mb-1">
                                    {demo.affinity}%
                                </div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {demo.segment}
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                                    <div
                                        className="bg-indigo-500 h-2 rounded-full transition-all duration-1000"
                                        style={{ width: `${demo.affinity}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Recommended Price Points */}
            {aiInsights && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        AI-Recommended Price Architecture
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {aiInsights.recommended_price_points.map((price, idx) => (
                            <div key={idx} className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                                <div className="text-lg font-bold text-green-600 dark:text-green-400">
                                    ${price.toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                    {idx === 0 ? 'Entry' : idx === 1 ? 'Core' : idx === 2 ? 'Premium' : 'Statement'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CurationIntelligence;